import React, { useState, useEffect } from 'react';

const routerBasename = window.location.pathname.startsWith('/v2') ? '/v2' : undefined;
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { usePolling } from './hooks/usePolling';
import { alarmsAPI } from './services/api';

import LoginPage    from './pages/LoginPage';
import InfraPage    from './pages/InfraPage';
import ClientsPage  from './pages/ClientsPage';
import ChildrenPage from './pages/ChildrenPage';
import TrackersPage from './pages/TrackersPage';
import AlarmsPage   from './pages/AlarmsPage';
import LocationPage from './pages/LocationPage';
import OperatorsPage from './pages/OperatorsPage';
import CommercialPage from './pages/CommercialPage';
import PaymentsPage from './pages/PaymentsPage';

const ROLE_LABELS = {
  super_admin: 'Super administrateur',
  admin_ops: 'Administrateur operations',
  superviseur: 'Superviseur support',
  support_n2: 'Support niveau 2',
  support_n1: 'Support niveau 1',
};

export function canAccess(user, permissions = []) {
  if (!permissions.length) return true;
  const granted = Array.isArray(user?.permissions) ? user.permissions : [];
  return permissions.every(permission => granted.includes(permission));
}

function AccessDenied() {
  return (
    <div style={S.accessDenied}>
      <div style={S.accessIcon}>!</div>
      <div>
        <h2 style={S.accessTitle}>Acces limite</h2>
        <p style={S.accessText}>
          Cette section contient des informations personnelles sensibles. Votre profil metier ne permet pas d'y acceder.
        </p>
      </div>
    </div>
  );
}

function RequirePermission({ permissions, children }) {
  const { user } = useAuth();
  return canAccess(user, permissions) ? children : <AccessDenied />;
}

// -- Protected route wrapper --------------------------------------------------
function Protected({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div style={{ background: '#0d1b3e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f5a623', fontFamily: 'monospace' }}>Chargement...</div>;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// -- Device-conflict modal ----------------------------------------------------
function DeviceConflictModal() {
  const { deviceConflict, clearConflict, logout } = useAuth();
  if (!deviceConflict) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#10224a', border: '1px solid #e63946', borderRadius: 10, padding: 32, maxWidth: 380, fontFamily: 'monospace', textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>!</div>
        <div style={{ color: '#e63946', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Session expiree</div>
        <div style={{ color: 'rgba(255,255,255,.72)', fontSize: 12, marginBottom: 20 }}>Ce compte est deja utilise sur un autre appareil. Reconnectez-vous.</div>
        <button onClick={logout} style={{ background: '#1e4db7', color: '#fff', border: 'none', fontFamily: 'monospace', fontSize: 13, fontWeight: 700, padding: '10px 24px', borderRadius: 6, cursor: 'pointer' }}>
          Se reconnecter
        </button>
        <button onClick={clearConflict} style={{ marginLeft: 8, background: 'transparent', color: 'rgba(255,255,255,.72)', border: '1px solid rgba(255,255,255,.16)', fontFamily: 'monospace', fontSize: 13, fontWeight: 700, padding: '10px 18px', borderRadius: 6, cursor: 'pointer' }}>
          Fermer
        </button>
      </div>
    </div>
  );
}

// -- Main shell ---------------------------------------------------------------
function Shell() {
  const { user, logout } = useAuth();
  const [alarmCount, setAlarmCount] = useState(0);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll alarms count
  usePolling(async () => {
    try {
      const res = await alarmsAPI.getActive();
      setAlarmCount((res.data || []).filter(a => !a.acked).length);
    } catch { setAlarmCount(0); }
  }, 15000);

  const navItems = [
    { to: '/infra',    label: 'Infrastructure', icon: '⬡', permissions: ['monitor:view'] },
    { to: '/alarms',   label: 'Alarmes',        icon: '△', badge: alarmCount, permissions: ['alerts:view'] },
    { to: '/clients',  label: 'Abonnes',        icon: '◎', permissions: ['clients:view'] },
    { to: '/children', label: 'Enfants',        icon: '◇', permissions: ['children:view'] },
    { to: '/trackers', label: 'Traceurs',       icon: '▣', permissions: ['trackers:view'] },
    { to: '/location', label: 'Localisation',   icon: '◉', permissions: ['location:view'] },
    { to: '/commercial', label: 'Terrain',        icon: '+', permissions: ['commercial:view'] },
    { to: '/payments', label: 'Paiements', icon: '¤', permissions: ['operators:manage'] },
    { to: '/operators', label: 'Utilisateurs',   icon: '□', permissions: ['operators:manage'] },
  ];

  const visibleNavItems = navItems.filter(n => canAccess(user, n.permissions));
  const roleLabel = user?.roleLabel || ROLE_LABELS[user?.role] || user?.role || 'Operateur';

  return (
    <div style={S.app}>
      <DeviceConflictModal />

      {/* Sidebar */}
      <div style={S.sidebar}>
        <div style={S.logoBlock}>
          <div style={S.logoBadge}>TOKKAM</div>
          <div style={S.logoSub}>monitoring v2.0</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {visibleNavItems.map(n => (
            <NavLink key={n.to} to={n.to} style={({ isActive }) => ({ ...S.navItem, ...(isActive ? S.navActive : {}) })}>
              <span style={S.navIcon}>{n.icon}</span>
              <span>{n.label}</span>
              {n.badge > 0 && <span style={S.navBadge}>{n.badge}</span>}
            </NavLink>
          ))}
        </nav>
        <div style={S.sideFooter}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={S.statusDot}/>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.72)' }}>Systemes nominaux</span>
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.58)', marginBottom: 6 }}>
            {user?.username || user?.phoneNumber || 'Operateur'} · {roleLabel}
          </div>
          <button style={S.logoutBtn} onClick={logout}>⊗ Deconnexion</button>
        </div>
      </div>

      {/* Main */}
      <div style={S.main}>
        {/* Topbar */}
        <div style={S.topbar}>
          <div style={S.topbarTitle}>
            <Routes>
              <Route path="/infra"    element={<span>INFRASTRUCTURE</span>} />
              <Route path="/clients"  element={<span>ABONNES</span>} />
              <Route path="/children" element={<span>ENFANTS SUIVIS</span>} />
              <Route path="/trackers" element={<span>TRACEURS</span>} />
              <Route path="/alarms"   element={<span>ALARMES & ALERTES</span>} />
              <Route path="/location" element={<span>LOCALISATION</span>} />
              <Route path="/commercial" element={<span>TERRAIN COMMERCIAL</span>} />
              <Route path="/payments" element={<span>PAIEMENTS</span>} />
              <Route path="/operators" element={<span>UTILISATEURS & PROFILS</span>} />
              <Route path="*"         element={<span>TOKKAM MONITOR</span>} />
            </Routes>
          </div>
          <span style={S.clock}>
            {time.toLocaleTimeString('fr-FR', { hour12: false })} · {time.toLocaleDateString('fr-FR')}
          </span>
          {alarmCount > 0 && (
            <NavLink to="/alarms" style={S.alarmBell}>
              △ {alarmCount} alerte{alarmCount > 1 ? 's' : ''}
            </NavLink>
          )}
        </div>

        {/* Content */}
        <div style={S.content}>
          <Routes>
            <Route path="/"         element={<Navigate to="/infra" replace />} />
            <Route path="/infra"    element={<InfraPage />} />
            <Route path="/alarms"   element={<AlarmsPage />} />
            <Route path="/clients"  element={<RequirePermission permissions={['clients:view']}><ClientsPage /></RequirePermission>} />
            <Route path="/children" element={<RequirePermission permissions={['children:view']}><ChildrenPage /></RequirePermission>} />
            <Route path="/trackers" element={<RequirePermission permissions={['trackers:view']}><TrackersPage /></RequirePermission>} />
            <Route path="/location" element={<RequirePermission permissions={['location:view']}><LocationPage /></RequirePermission>} />
            <Route path="/commercial" element={<RequirePermission permissions={['commercial:view']}><CommercialPage /></RequirePermission>} />
            <Route path="/payments" element={<RequirePermission permissions={['operators:manage']}><PaymentsPage /></RequirePermission>} />
            <Route path="/operators" element={<RequirePermission permissions={['operators:manage']}><OperatorsPage /></RequirePermission>} />
            <Route path="/client-stats" element={<Navigate to="/infra" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

// -- Root ---------------------------------------------------------------------
export default function App() {
  return (
    <BrowserRouter basename={routerBasename}>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: '#10224a', color: '#ffffff', border: '1px solid rgba(255,255,255,.14)', fontFamily: 'monospace', fontSize: 12 },
            success: { iconTheme: { primary: '#22c55e', secondary: '#0d1b3e' } },
            error:   { iconTheme: { primary: '#e63946', secondary: '#0d1b3e' } },
          }}
        />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*"     element={<Protected><Shell /></Protected>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

const S = {
  app:     { display: 'flex', height: '100vh', overflow: 'hidden', background: '#0d1b3e', fontFamily: "'IBM Plex Mono','Courier New',monospace", color: '#ffffff' },
  sidebar: { width: 200, background: '#10224a', borderRight: '1px solid rgba(255,255,255,.14)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logoBlock: { padding: '16px 14px 12px', borderBottom: '1px solid rgba(255,255,255,.14)' },
  logoBadge: { display: 'inline-block', fontSize: 13, fontWeight: 700, letterSpacing: 3, color: '#f5a623', background: 'rgba(88,166,255,.08)', border: '1px solid rgba(88,166,255,.2)', padding: '4px 10px', borderRadius: 4 },
  logoSub: { fontSize: 10, color: 'rgba(255,255,255,.58)', marginTop: 5, letterSpacing: .5 },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', color: 'rgba(255,255,255,.72)', fontSize: 12, letterSpacing: .3, textDecoration: 'none', borderLeft: '2px solid transparent', transition: 'all .15s' },
  navActive: { color: '#f5a623', background: 'rgba(88,166,255,.07)', borderLeftColor: '#f5a623' },
  navIcon:  { width: 16, textAlign: 'center', fontSize: 14 },
  navBadge: { marginLeft: 'auto', background: '#e63946', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 10, fontWeight: 700 },
  sideFooter: { padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,.14)' },
  statusDot:  { width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e', display: 'inline-block' },
  logoutBtn:  { background: 'rgba(248,81,73,.08)', border: '1px solid rgba(248,81,73,.2)', color: '#e63946', fontFamily: 'monospace', fontSize: 11, padding: '5px 10px', borderRadius: 4, cursor: 'pointer', width: '100%' },
  main:    { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar:  { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', background: '#10224a', borderBottom: '1px solid rgba(255,255,255,.14)', flexShrink: 0 },
  topbarTitle: { flex: 1, fontSize: 13, fontWeight: 700, letterSpacing: 1, color: '#ffffff' },
  clock:   { fontSize: 11, color: 'rgba(255,255,255,.58)', fontFamily: 'monospace' },
  alarmBell: { background: 'rgba(248,81,73,.12)', border: '1px solid rgba(248,81,73,.3)', color: '#e63946', fontSize: 11, padding: '4px 12px', borderRadius: 4, textDecoration: 'none', fontFamily: 'monospace', animation: 'pulse 2s infinite' },
  content: { flex: 1, overflowY: 'auto' },
  accessDenied: { margin: 24, padding: 24, border: '1px solid rgba(245,166,35,.35)', background: 'rgba(245,166,35,.08)', borderRadius: 10, display: 'flex', gap: 16, alignItems: 'flex-start', maxWidth: 680 },
  accessIcon: { width: 34, height: 34, borderRadius: '50%', background: 'rgba(245,166,35,.16)', color: '#f5a623', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, flexShrink: 0 },
  accessTitle: { margin: 0, color: '#f5a623', fontSize: 18, letterSpacing: .2 },
  accessText: { margin: '8px 0 0', color: 'rgba(255,255,255,.72)', fontSize: 13, lineHeight: 1.6 },
};
